// src/components/pages/DeleteAccountInfo.jsx
import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useSelector } from 'react-redux';
import { 
  FaTrash, 
  FaLock, 
  FaUserSlash, 
  FaExclamationTriangle,
  FaArrowLeft, 
  FaHome, 
  FaCheckCircle, 
  FaClock,
  FaShieldAlt, 
  FaDatabase, 
  FaUserSecret, 
  FaFileAlt,
  FaEnvelope, 
  FaPhone, 
  FaMapMarkerAlt, 
  FaUtensils, 
  FaHeart,
  FaInfoCircle, 
  FaStepForward, 
  FaUserCheck, 
  FaTimesCircle,
  FaUserCircle,
  FaCog,
  FaUserEdit,
  FaMobileAlt,
  FaArrowRight
} from 'react-icons/fa';
import { getColors } from '../../styles/colors';

const DeleteAccountInfo = () => {
  const navigate = useNavigate();
  const theme = useSelector((state) => state.theme.mode);
  const isDark = theme === 'dark';
  const colors = getColors(isDark);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5 }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  // ✅ Updated steps for App process
  const steps = [
    {
      icon: <FaCog />,
      title: 'Step 1: Open Settings',
      description: 'Launch the Foodies Tiffin app and tap on the Settings icon (⚙️) located in the bottom navigation bar or in the profile section.',
      platform: 'App'
    },
    {
      icon: <FaUserEdit />,
      title: 'Step 2: Go to Personal Information',
      description: 'In the Settings menu, scroll down and select "Personal Information" or "Account Settings" to view and manage your profile details.',
      platform: 'App'
    },
    {
      icon: <FaTrash />,
      title: 'Step 3: Press Delete Account',
      description: 'Scroll to the bottom of the Personal Information page and tap on the "Delete Account" button. You will be asked to confirm your decision.',
      platform: 'App'
    },
    {
      icon: <FaExclamationTriangle />,
      title: 'Step 4: Review the Warning',
      description: 'A warning message will appear explaining that this action is permanent. Read the information carefully before proceeding.',
      platform: 'App'
    },
    {
      icon: <FaCheckCircle />,
      title: 'Step 5: Confirm Deletion',
      description: 'Enter your password or confirm your identity, then tap "Yes, Delete My Account" to permanently remove your account.',
      platform: 'App'
    },
    {
      icon: <FaClock />,
      title: 'Step 6: Account Deletion Complete',
      description: 'Your account will be permanently deleted. You will be logged out and redirected to the login screen.',
      platform: 'App'
    }
  ];

  // ✅ Web process steps
  const webSteps = [
    {
      icon: <FaUserCircle />,
      title: 'Step 1: Login to Your Account',
      description: 'Navigate to the Foodies Tiffin website and log in with your credentials.',
      platform: 'Web'
    },
    {
      icon: <FaUserCircle />,
      title: 'Step 2: Go to Account Dropdown',
      description: 'Click on your profile icon in the top right corner of the navbar to open the account dropdown menu.',
      platform: 'Web'
    },
    {
      icon: <FaTrash />,
      title: 'Step 3: Click on "Delete Account"',
      description: 'In the dropdown menu, you will find the "Delete Account" option. Click on it to proceed.',
      platform: 'Web'
    },
    {
      icon: <FaExclamationTriangle />,
      title: 'Step 4: Review the Warning',
      description: 'You will be shown important information about what will be deleted. Read this carefully before proceeding.',
      platform: 'Web'
    },
    {
      icon: <FaCheckCircle />,
      title: 'Step 5: Confirm Deletion',
      description: 'Type "DELETE" in the confirmation field and click the "Delete Account" button to confirm your request.',
      platform: 'Web'
    },
    {
      icon: <FaClock />,
      title: 'Step 6: Account Deletion Complete',
      description: 'Your account will be permanently deleted. You will be redirected to the home page.',
      platform: 'Web'
    }
  ];

  const whatGetsDeleted = [
    {
      icon: <FaUserSecret />,
      title: 'Personal Information',
      description: 'Your name, email address, phone number, and profile picture will be permanently removed.'
    },
    {
      icon: <FaDatabase />,
      title: 'Order History',
      description: 'All your past orders, delivery addresses, and order preferences will be deleted from our system.'
    },
    {
      icon: <FaFileAlt />,
      title: 'Saved Data',
      description: 'Saved payment methods, favourite items, and any custom preferences will be removed.'
    },
    {
      icon: <FaUtensils />,
      title: 'Tiffin Subscriptions',
      description: 'All active and past tiffin subscriptions and bookings will be cancelled and deleted.'
    }
  ];

  const whatStays = [
    {
      icon: <FaLock />,
      title: 'Anonymized Data',
      description: 'Some anonymized data may be retained for analytical purposes, but it cannot be linked to you.'
    },
    {
      icon: <FaShieldAlt />,
      title: 'Legal Obligations',
      description: 'We may retain certain data if required by law or for fraud prevention purposes.'
    }
  ];

  return (
    <div className="min-h-screen py-8 px-4" style={{ backgroundColor: colors.background }}>
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <div className="mb-6">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-sm font-medium transition-all hover:scale-105"
            style={{ color: colors.primary }}
          >
            <FaArrowLeft className="text-xs" /> Back
          </button>
        </div>

        {/* Header */}
        <motion.div 
          className="text-center mb-10"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="inline-flex items-center gap-2 bg-red-50 dark:bg-red-900/20 backdrop-blur-sm px-4 py-2 rounded-full text-xs font-medium shadow-sm mb-4" style={{ color: '#EF4444' }}>
            <FaTrash className="text-[10px]" /> Delete Account
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold mb-3" style={{ color: colors.text }}>
            Account Deletion Guide
          </h1>
          <p className="text-sm" style={{ color: colors.textSecondary }}>
            Learn how to permanently delete your Foodies Tiffin account
          </p>
          <div className="flex items-center justify-center gap-4 mt-4 text-xs" style={{ color: colors.textSecondary }}>
            <span className="flex items-center gap-1">
              <FaExclamationTriangle className="text-[10px]" style={{ color: '#EF4444' }} /> 
              Permanent Action
            </span>
            <span className="w-px h-4" style={{ backgroundColor: colors.border }}></span>
            <span className="flex items-center gap-1">
              <FaClock className="text-[10px]" style={{ color: colors.primary }} /> 
              Takes 30 Days
            </span>
          </div>
        </motion.div>

        {/* Warning Banner */}
        <motion.div 
          className="p-6 rounded-2xl mb-8"
          style={{ 
            backgroundColor: '#FEF2F2',
            border: '2px solid #FECACA'
          }}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
        >
          <div className="flex items-start gap-3">
            <FaExclamationTriangle className="text-2xl text-red-500 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-bold text-red-700 dark:text-red-400">⚠️ Important Notice</h3>
              <p className="text-sm text-red-600 dark:text-red-300 mt-1">
                Deleting your account is a <strong>permanent action</strong> that cannot be undone. 
                All your data, including order history, saved addresses, and tiffin subscriptions, 
                will be permanently removed from our system.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Platform Navigation - Clean Links without Buttons */}
        <motion.div 
          className="flex flex-wrap gap-2 mb-8 p-4 rounded-xl"
          style={{ backgroundColor: colors.card, border: `1px solid ${colors.border}` }}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
        >
          <span className="text-xs font-medium flex items-center gap-1.5" style={{ color: colors.textSecondary }}>
            <FaInfoCircle className="text-[10px]" /> Jump to:
          </span>
          <a href="#app-steps" className="text-[10px] px-2.5 py-1 rounded-full transition-all hover:scale-105 flex items-center gap-1" style={{ backgroundColor: colors.inputBackground, color: colors.textSecondary, border: `1px solid ${colors.border}` }}>
            <FaMobileAlt className="text-[8px]" /> App Steps
          </a>
          <a href="#web-steps" className="text-[10px] px-2.5 py-1 rounded-full transition-all hover:scale-105 flex items-center gap-1" style={{ backgroundColor: colors.inputBackground, color: colors.textSecondary, border: `1px solid ${colors.border}` }}>
            <FaUserCircle className="text-[8px]" /> Web Steps
          </a>
          <a href="#what-deleted" className="text-[10px] px-2.5 py-1 rounded-full transition-all hover:scale-105" style={{ backgroundColor: colors.inputBackground, color: colors.textSecondary, border: `1px solid ${colors.border}` }}>
            What's Deleted
          </a>
          <a href="#what-stays" className="text-[10px] px-2.5 py-1 rounded-full transition-all hover:scale-105" style={{ backgroundColor: colors.inputBackground, color: colors.textSecondary, border: `1px solid ${colors.border}` }}>
            What Stays
          </a>
        </motion.div>

        {/* App Steps Section */}
        <motion.div 
          id="app-steps"
          className="mb-10"
          variants={staggerContainer}
          initial="initial"
          animate="animate"
        >
          <div className="flex items-center gap-3 mb-4">
            <FaMobileAlt className="text-2xl" style={{ color: colors.primary }} />
            <h2 className="text-2xl font-bold" style={{ color: colors.text }}>
              How to Delete on <span style={{ color: colors.primary }}>App</span>
            </h2>
          </div>
          <div className="space-y-4">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                className="rounded-2xl overflow-hidden transition-all hover:shadow-md"
                style={{ 
                  backgroundColor: colors.card,
                  border: `1px solid ${colors.border}`,
                }}
                variants={fadeInUp}
              >
                <div className="p-5">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: colors.primary + '20', color: colors.primary }}>
                      {step.icon}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-sm" style={{ color: colors.text }}>
                          <span className="text-primary mr-1">#{index + 1}</span>
                          {step.title}
                        </h3>
                        <span className="text-[9px] px-2 py-0.5 rounded-full font-medium" style={{ 
                          backgroundColor: '#3B82F6',
                          color: '#fff'
                        }}>
                          {step.platform}
                        </span>
                      </div>
                      <p className="text-sm mt-0.5" style={{ color: colors.textSecondary }}>
                        {step.description}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Web Steps Section */}
        <motion.div 
          id="web-steps"
          className="mb-10"
          variants={staggerContainer}
          initial="initial"
          animate="animate"
        >
          <div className="flex items-center gap-3 mb-4">
            <FaUserCircle className="text-2xl" style={{ color: colors.primary }} />
            <h2 className="text-2xl font-bold" style={{ color: colors.text }}>
              How to Delete on <span style={{ color: colors.primary }}>Web</span>
            </h2>
          </div>
          <div className="space-y-4">
            {webSteps.map((step, index) => (
              <motion.div
                key={index}
                className="rounded-2xl overflow-hidden transition-all hover:shadow-md"
                style={{ 
                  backgroundColor: colors.card,
                  border: `1px solid ${colors.border}`,
                }}
                variants={fadeInUp}
              >
                <div className="p-5">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: colors.primary + '20', color: colors.primary }}>
                      {step.icon}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-sm" style={{ color: colors.text }}>
                          <span className="text-primary mr-1">#{index + 1}</span>
                          {step.title}
                        </h3>
                        <span className="text-[9px] px-2 py-0.5 rounded-full font-medium" style={{ 
                          backgroundColor: '#8B5CF6',
                          color: '#fff'
                        }}>
                          {step.platform}
                        </span>
                      </div>
                      <p className="text-sm mt-0.5" style={{ color: colors.textSecondary }}>
                        {step.description}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* What Gets Deleted */}
        <motion.div 
          id="what-deleted"
          className="mb-8"
          variants={staggerContainer}
          initial="initial"
          animate="animate"
        >
          <h2 className="text-2xl font-bold mb-4 flex items-center gap-2" style={{ color: colors.text }}>
            <FaDatabase className="text-xl" style={{ color: '#EF4444' }} />
            What Gets Deleted
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {whatGetsDeleted.map((item, index) => (
              <motion.div
                key={index}
                className="rounded-2xl overflow-hidden transition-all hover:shadow-md"
                style={{ 
                  backgroundColor: colors.card,
                  border: `1px solid ${colors.border}`,
                }}
                variants={fadeInUp}
              >
                <div className="p-5">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#FEF2F2', color: '#EF4444' }}>
                      {item.icon}
                    </div>
                    <div>
                      <h3 className="font-semibold text-sm" style={{ color: colors.text }}>
                        {item.title}
                      </h3>
                      <p className="text-sm mt-0.5" style={{ color: colors.textSecondary }}>
                        {item.description}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* What Stays */}
        <motion.div 
          id="what-stays"
          className="mb-8"
          variants={staggerContainer}
          initial="initial"
          animate="animate"
        >
          <h2 className="text-2xl font-bold mb-4 flex items-center gap-2" style={{ color: colors.text }}>
            <FaLock className="text-xl" style={{ color: '#22C55E' }} />
            What Stays
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {whatStays.map((item, index) => (
              <motion.div
                key={index}
                className="rounded-2xl overflow-hidden transition-all hover:shadow-md"
                style={{ 
                  backgroundColor: colors.card,
                  border: `1px solid ${colors.border}`,
                }}
                variants={fadeInUp}
              >
                <div className="p-5">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#F0FDF4', color: '#22C55E' }}>
                      {item.icon}
                    </div>
                    <div>
                      <h3 className="font-semibold text-sm" style={{ color: colors.text }}>
                        {item.title}
                      </h3>
                      <p className="text-sm mt-0.5" style={{ color: colors.textSecondary }}>
                        {item.description}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Timeline Info */}
        <motion.div 
          className="p-5 rounded-2xl mb-8"
          style={{ 
            backgroundColor: colors.card,
            border: `1px solid ${colors.border}`
          }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
        >
          <div className="flex items-start gap-3">
            <FaClock className="text-xl mt-0.5" style={{ color: colors.primary }} />
            <div>
              <h3 className="font-semibold text-sm" style={{ color: colors.text }}>
                Data Deletion Timeline
              </h3>
              <p className="text-sm mt-1" style={{ color: colors.textSecondary }}>
                Once you confirm account deletion, your data will be marked for deletion. 
                The complete removal process takes <strong>30 days</strong>. During this time, 
                you can contact us to cancel the deletion request if you change your mind.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Contact Support - Informational Only, No Buttons */}
        <motion.div 
          className="p-5 rounded-2xl text-center"
          style={{ 
            backgroundColor: colors.card,
            border: `1px solid ${colors.border}`
          }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.8 }}
        >
          <h3 className="text-sm font-medium mb-2" style={{ color: colors.text }}>
            Need Help?
          </h3>
          <p className="text-sm" style={{ color: colors.textSecondary }}>
            If you have any questions about the account deletion process, please contact our support team.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4 mt-3 text-sm">
            <span className="flex items-center gap-2" style={{ color: colors.textSecondary }}>
              <FaEnvelope style={{ color: colors.primary }} /> 
              support@foodiestiffin.com
            </span>
            <span className="w-px h-4" style={{ backgroundColor: colors.border }}></span>
            <span className="flex items-center gap-2" style={{ color: colors.textSecondary }}>
              <FaPhone style={{ color: colors.primary }} /> 
              +91 98765 43210
            </span>
          </div>
        </motion.div>

        {/* Footer - Navigation Links Only */}
        <motion.div 
          className="mt-8 text-center text-xs"
          style={{ color: colors.textSecondary + '80' }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 1 }}
        >
          <p className="flex items-center justify-center gap-1">
            <FaUtensils className="text-[10px]" style={{ color: colors.primary }} />
            © {new Date().getFullYear()} Foodies Tiffin Service. All rights reserved.
            <FaHeart className="text-[10px]" style={{ color: '#EF4444' }} />
          </p>
          <div className="flex items-center justify-center gap-4 mt-2">
            <Link to="/" className="hover:underline" style={{ color: colors.primary }}>
              <FaHome className="inline mr-1 text-[10px]" /> Home
            </Link>
            <span className="w-px h-3" style={{ backgroundColor: colors.border }}></span>
            <Link to="/privacy" className="hover:underline" style={{ color: colors.primary }}>
              Privacy Policy
            </Link>
            <span className="w-px h-3" style={{ backgroundColor: colors.border }}></span>
            <Link to="/delete-account-info" className="hover:underline" style={{ color: colors.primary }}>
              Delete Account
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default DeleteAccountInfo;